import React, { useState, useRef } from 'react';
import { MainLayout } from '../../components/layout/MainLayout';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { patternRequestService } from '../../api/services/patternRequestService';

interface PatternFormData {
  requestDate: string;
  patternReceivedDate: string;
  digitizedDate: string;
  dateSentToCutter: string;
  patternType: string;
  patternToMadeBy: string;
  year: string;
  make: string;
  model: string;
  submodel: string;
  body: string;
  partNumber: string;
  seatType: string;
  seatPosition: string;
  seatOptions: string[];
  additionalOptions: string;
  approvedBy: string;
  approvalDate: string;
}

const seatTypes = [
  'Bucket',
  'Sport bucket',
  'Solid Bench',
  '40/60 Split Bench',
  '60/40 Split Bench',
  '50/50 Split Bench',
  '40/20/40 Split Bench',
  '40/20/40 Sport Bench',
];

const seatOptions = [
  // Column 1 - Headrest
  'w/ Separate headrest',
  'w/ Built-in headrest',
  'w/o Separate headrest',
  'w/ Folding headrest',
  'w/o Folding headrest',
  'w/ Non removable headrest',
  // Column 1 - Airbags/Seatbelt
  'w/ Side Airbags',
  'w/o Side Airbags',
  'w/ Built-in Seatbelt',
  'w/o Built-in Seatbelt',
  // Column 2 - Armrest
  'w/ Armrest',
  'w/ Dual Armrest',
  'w/ Driver Armrest',
  'w/ Underseat storage',
  'w/o Underseat storage',
  // Column 2 - Lumbar
  'w/ Backrest Lumbar',
  'w/ Cushion Lumbar',
  'w/ Lumbar',
  'w/o Lumbar',
  'w/ Driver Lumbar',
  // Column 3 - Lumbar cont.
  'w/ Dual Lumbar',
  'w/ Electric Lumbar',
  'w/ Dual Electric Lumbar',
  'w/ Manual Lumbar',
  // Column 3 - Backrest/Cushion
  'w/ Solid Backrest',
  'w/ Solid Cushion',
  'w/ 40/60 Cushion',
  'w/ 60/40 Cushion',
  'w/ Side pannels',
  // Column 4 - Cup holders/backrest
  'w/ Cup holders',
  'w/o Cup holders',
  'w/ Folding backrest',
  'w/o Folding backrest',
  'w/ Reclining latch',
  'w/o Reclining latch',
];

const NewPatternForm: React.FC = () => {
  const formRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<PatternFormData>({
    requestDate: new Date().toISOString().split('T')[0],
    patternReceivedDate: '',
    digitizedDate: '',
    dateSentToCutter: '',
    patternType: '',
    patternToMadeBy: '',
    year: '',
    make: '',
    model: '',
    submodel: '',
    body: '',
    partNumber: '',
    seatType: '',
    seatPosition: '',
    seatOptions: [],
    additionalOptions: '',
    approvedBy: '',
    approvalDate: '',
  });

  const submitMutation = useMutation({
    mutationFn: patternRequestService.submitPatternRequest,
    onSuccess: () => {
      toast.success('Pattern request submitted successfully! Email sent.');
      // Reset form
      setFormData({
        requestDate: new Date().toISOString().split('T')[0],
        patternReceivedDate: '',
        digitizedDate: '',
        dateSentToCutter: '',
        patternType: '',
        patternToMadeBy: '',
        year: '',
        make: '',
        model: '',
        submodel: '',
        body: '',
        partNumber: '',
        seatType: '',
        seatPosition: '',
        seatOptions: [],
        additionalOptions: '',
        approvedBy: '',
        approvalDate: '',
      });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to submit pattern request');
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSeatOptionToggle = (option: string) => {
    setFormData(prev => ({
      ...prev,
      seatOptions: prev.seatOptions.includes(option)
        ? prev.seatOptions.filter(o => o !== option)
        : [...prev.seatOptions, option],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.patternType) {
      toast.error('Please enter a Pattern Type');
      return;
    }
    if (!formData.year || !formData.make || !formData.model) {
      toast.error('Please enter Year, Make, and Model');
      return;
    }
    if (!formData.partNumber) {
      toast.error('Please enter a Part Number');
      return;
    }
    if (!formData.seatPosition) {
      toast.error('Please select a Seat Position');
      return;
    }
    if (!formData.seatType) {
      toast.error('Please select a Seat Type');
      return;
    }

    submitMutation.mutate(formData);
  };

  return (
    <MainLayout>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Request for New Pattern
            </h1>
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              Fill out the form below to request a new pattern. The form will be emailed upon submission.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-6">
          <div ref={formRef} className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border-2 border-gray-300 dark:border-gray-600">
            {/* Header */}
            <div className="border-b-2 border-gray-800 dark:border-gray-400 pb-4 mb-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white underline">
                  NEW PATTERN SUBMISSION FORM
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">DATE:</span>
                  <input
                    type="date"
                    name="requestDate"
                    value={formData.requestDate}
                    onChange={handleInputChange}
                    className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Tracking Dates Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  PATTERN RECEIVED:
                </label>
                <input
                  type="date"
                  name="patternReceivedDate"
                  value={formData.patternReceivedDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  DIGITIZED DATE:
                </label>
                <input
                  type="date"
                  name="digitizedDate"
                  value={formData.digitizedDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  DATE SENT TO CUTTER:
                </label>
                <input
                  type="date"
                  name="dateSentToCutter"
                  value={formData.dateSentToCutter}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-white"
                />
              </div>
            </div>

            {/* Main Form Content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Vehicle Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    PATTERN TYPE:
                  </label>
                  <input
                    type="text"
                    name="patternType"
                    value={formData.patternType}
                    onChange={handleInputChange}
                    placeholder="e.g., SEAT COVER"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    PATTERN TO BE MADE BY:
                  </label>
                  <input
                    type="text"
                    name="patternToMadeBy"
                    value={formData.patternToMadeBy}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    YEAR: <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="year"
                    value={formData.year}
                    onChange={handleInputChange}
                    placeholder="e.g., 2014"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    MAKE: <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="make"
                    value={formData.make}
                    onChange={handleInputChange}
                    placeholder="e.g., FORD"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    MODEL: <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    placeholder="e.g., TAURUS"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    SUBMODEL:
                  </label>
                  <input
                    type="text"
                    name="submodel"
                    value={formData.submodel}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    BODY:
                  </label>
                  <input
                    type="text"
                    name="body"
                    value={formData.body}
                    onChange={handleInputChange}
                    placeholder="e.g., SEDAN"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    PART #: <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="partNumber"
                    value={formData.partNumber}
                    onChange={handleInputChange}
                    placeholder="e.g., FD470"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              {/* Right Column - Seat Type */}
              <div>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 border-b pb-2">
                    CHOOSE SEAT TYPE <span className="text-red-500">*</span>
                  </h3>
                  <div className="space-y-2">
                    {seatTypes.map((type) => (
                      <label key={type} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="seatType"
                          value={type}
                          checked={formData.seatType === type}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-indigo-600 border-gray-300 dark:border-gray-600 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Seat Position */}
            <div className="mt-6">
              <label className="block text-sm font-bold text-gray-900 dark:text-white mb-2">
                SEAT POSITION: <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-4">
                {['FRONT', 'MIDDLE', 'REAR', 'THIRD ROW', 'CARGO MAT', 'DASH COVER', 'FLOOR MAT'].map((position) => (
                  <label key={position} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="seatPosition"
                      value={position}
                      checked={formData.seatPosition === position}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-indigo-600 border-gray-300 dark:border-gray-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{position}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Seat Options */}
            <div className="mt-6 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4 border-b pb-2">
                CHOOSE ALL POSSIBLE SEAT OPTIONS: {formData.seatPosition || ''}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {seatOptions.map((option) => (
                  <label key={option} className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={formData.seatOptions.includes(option)}
                      onChange={() => handleSeatOptionToggle(option)}
                      className="h-4 w-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-xs text-gray-700 dark:text-gray-300">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Additional Options */}
            <div className="mt-6">
              <label className="block text-sm font-bold text-gray-900 dark:text-white mb-2">
                PLEASE LIST ADDITIONAL OPTIONS:
              </label>
              <textarea
                name="additionalOptions"
                value={formData.additionalOptions}
                onChange={handleInputChange}
                rows={4}
                placeholder="Enter any additional options or notes..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Approval Section */}
            <div className="mt-6 pt-4 border-t border-gray-300 dark:border-gray-600">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    APPROVED BY:
                  </label>
                  <input
                    type="text"
                    name="approvedBy"
                    value={formData.approvedBy}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    DATE:
                  </label>
                  <input
                    type="date"
                    name="approvalDate"
                    value={formData.approvalDate}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8 flex justify-end">
              <button
                type="submit"
                disabled={submitMutation.isPending}
                className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitMutation.isPending ? 'Submitting...' : 'Submit Pattern Request'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default NewPatternForm;
